import React, { useContext, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import algoliasearch from 'algoliasearch';
import cloneDeep from 'lodash.clonedeep';
import { v4 as uuidv4 } from 'uuid';
import { Configure, InstantSearch } from 'react-instantsearch-hooks';
import AccountDefaultLogo from 'assets/images/account-avatar.svg';
import './statics.scss';
import {
  AddContentLeftContainer,
  AddContentRightContainer,
  ContainerTitle,
  ContentItemsContainer,
  CourseItem,
  CustomContentLoadingWrapper,
  CustomTrainingTabLabel,
  CustomTrainingTabLogo,
  FiltersWrapper,
  IconWrapper,
  LinksContainer,
} from './styles';
import {
  HeaderContainer,
  ModalActions,
  ModalContainer,
  ModalContent,
  ModalSection,
} from 'components/modal/styles';
import { IPlaylist, IPlaylistLink } from 'contexts/playlists/types';
import Button from 'components/button';
import { FIRST_PLAYLIST_CATEGORY } from 'constants/settings';
import { INE_UUID } from 'constants/settings';
import Icon from 'components/icons';
import LinkItem from './LinkItem';
import Modal from 'components/modal';
import Notification from 'components/notification';
import { SearchInput } from './SearchInput';
import { SearchResults } from './SearchResults';
import CustomContentResults, { ICustomContentListItemView } from './CustomContentResults';
import { SessionContext } from 'contexts/session';
import { StyledInput } from 'components/inputs/styles';
import Tabs from 'components/tabs';
import { bulkCreateLinks } from 'services/links';
import { editPlaylist } from 'services/playlists';
import useCategoriesContent from 'hooks/useCategoriesContent';
import useLearningAreasContent from 'hooks/useLearningAreasContent';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import useDebounce from 'hooks/useDebounce';
import {
  CONTENT_TYPES,
  CONTENT_TYPE_OPTIONS,
  CUSTOM_CONTENT_PAGE_SIZE,
  CUSTOM_CONTENT_TAB,
  CUSTOM_CONTENT_TYPE_ALIASES,
  DEFAULT_ADD_CONTENT_TAB,
  DIFFICULTY_OPTIONS,
  ENABLED_CONTENT_TYPES,
  HAS_SKILL_CHECK_LAB,
  LINK_URL_REGEX,
  MAX_CUSTOM_CONTENT_PAGES,
  SKILL_CHECK_OPTIONS,
  createInitialFilters,
  getLabOptions,
} from 'constants/playlists';
import { CONTENT_TYPES as CONTENT_TYPE_SLUGS } from 'constants/content/content';
import InputSelectCheckbox from 'components/inputs/inputSelectCheckbox';
import FilterWithTooltip from './FilterWithTooltip';
import { useContentTypeLabel } from './ContentTypeTag';
import { ICustomContentItem, getCustomContentList } from 'services/customContent';
import { Spinner } from 'components/loadings';

interface IAddContentPlaylist {
  isVisible?: boolean;
  playlist: IPlaylist;
  category: string;
  onClose?: (playlist?: IPlaylist) => void;
  onGoBack?: () => void;
}

export interface IContentItem {
  id: string;
  name: string;
  content_type: string;
  objectID: string;
  slug: string;
  description: string;
  difficulty: string;
  duration: string;
  instructors: { name: string }[];
  amount_of_videos: number;
  amount_of_quizzes: number;
  amount_of_labs: number;
  amount_of_exercises: number;
  is_lab_experience?: boolean;
  has_skill_check_labs?: boolean;
}

export interface IFilters {
  content_type: string[];
  difficulty: string[];
  'categories.name': string[];
  'learning_areas.name': string[];
  is_lab_experience?: boolean;
  has_skill_check_labs?: boolean;
}

const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_APP_KEY
);

const AddContent: React.FC<IAddContentPlaylist> = ({ isVisible, playlist, category, onClose }) => {
  const title = 'Add content to your playlist';
  const ctaLabel = 'Add content';
  const [contentList, setContentList] = useState<Record<string, IContentItem>>({});
  const [linksList, setLinksList] = useState<Record<string, IPlaylistLink>>({});
  const [selectedCustomContent, setSelectedCustomContent] = useState<
    Record<string, ICustomContentListItemView>
  >({});
  const [customContentItems, setCustomContentItems] = useState<ICustomContentItem[]>([]);
  const [customContentSearch, setCustomContentSearch] = useState('');
  const [customContentTypeFilters, setCustomContentTypeFilters] = useState<string[]>([]);
  const [customContentLoading, setCustomContentLoading] = useState(false);
  const [customContentHasError, setCustomContentHasError] = useState(false);
  const [newLink, setNewLink] = useState('');
  const debouncedCustomContentSearch = useDebounce(customContentSearch, 300) as string;
  const isLinkValid = LINK_URL_REGEX.test(newLink);
  const showInvalidLink = newLink.length && !isLinkValid;
  const [filters, setFilters] = useState<IFilters>(() => createInitialFilters());
  const { learningAreas, getLearningAreas } = useLearningAreasContent();
  const learningAreaOptions = learningAreas.reduce(
    (acc, { name }) => {
      return { ...acc, [name]: { value: name, label: name } };
    },
    { '': { value: '', label: 'All' } }
  );
  const { categories, getCategories } = useCategoriesContent();
  const categoryOptions = categories.reduce(
    (acc, { name }) => {
      return { ...acc, [name]: { value: name, label: name } };
    },
    { '': { value: '', label: 'All' } }
  );
  const [facetFilters, setFacetFilters] = useState([]);
  const [selectedTab, setSelectedTab] = useState(DEFAULT_ADD_CONTENT_TAB);
  const [loading, setLoading] = useState(false);
  const { account } = useContext(SessionContext);
  const getContentTypeLabel = useContentTypeLabel();
  const business_account_id = account?.id;
  const collectionsEnabled = useBooleanFlag(
    FEATURE_FLAGS.SKILL_DIVE_COLLECTIONS_IN_PLAYLISTS,
    false
  );
  const customContentEnabled = useBooleanFlag(FEATURE_FLAGS.CUSTOM_CONTENT_ENABLED, false);
  const customContentEnabledM3 = useBooleanFlag(FEATURE_FLAGS.CUSTOM_CONTENT_ENABLED_M3, false);
  const isCustomContentTabEnabled = Boolean(customContentEnabled && customContentEnabledM3);
  const [contentAlreadyInPlaylist, setContentAlreadyInPlaylist] = useState<
    Record<string, IContentItem>
  >({});

  useEffect(() => {
    const obj = {};

    for (const section of playlist.content) {
      for (const item of section.content) {
        obj[item.uuid] = item;
      }
    }

    setContentAlreadyInPlaylist(obj);
  }, [playlist.content]);

  useEffect(() => {
    const { content_type } = filters;

    // Collections are indexed with is_lab_experience: true, so widening the
    // content_type clause reaches them wherever labs are in scope.
    const withCollections = (types: string[]) =>
      collectionsEnabled && types.includes(`content_type:${CONTENT_TYPES.LAB}`)
        ? [...types, `content_type:${CONTENT_TYPE_SLUGS.COLLECTION}`]
        : types;

    setFacetFilters(() => {
      if (content_type.length === 0) return [];
      const values = [];
      if (typeof filters['is_lab_experience'] !== 'undefined') {
        const labTypes =
          filters['is_lab_experience'] === true
            ? withCollections([`content_type:${CONTENT_TYPES.LAB}`])
            : [`content_type:${CONTENT_TYPES.LAB}`];
        values.push(labTypes, [`is_lab_experience:${filters['is_lab_experience']}`]);
        return values;
      } else if (typeof filters['has_skill_check_labs'] !== 'undefined') {
        const content_types = content_type.map(
          (ct) => `content_type:${ct === CONTENT_TYPES.LEARNING_PATH ? 'learning-path' : ct}`
        );
        values.push(content_types);
        values.push(['has_skill_check_labs:true']);
        return values;
      } else if (allValuesPresent(filters.content_type, ENABLED_CONTENT_TYPES)) {
        Object.entries(filters).map(([key, value]) => {
          const newarr = value.map(
            (v) => `${key}:${v.includes(CONTENT_TYPES.LEARNING_PATH) ? 'learning-path' : v}`
          );
          newarr.length > 0 && values.push(newarr);
        });
        return values;
      } else {
        const content_types = content_type.map(
          (ct) => `content_type:${ct === CONTENT_TYPES.LEARNING_PATH ? 'learning-path' : ct}`
        );
        values.push(withCollections(content_types));
        return values;
      }
    });
  }, [filters, collectionsEnabled]);

  useEffect(() => {
    getLearningAreas();
    getCategories();
  }, []);

  useEffect(() => {
    if (!isCustomContentTabEnabled && selectedTab === CUSTOM_CONTENT_TAB) {
      setSelectedTab(DEFAULT_ADD_CONTENT_TAB);
    }
  }, [isCustomContentTabEnabled, selectedTab]);

  const toNonEmptyString = (value: unknown): string | null => {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const normalized = `${value}`.trim();
    return normalized || null;
  };

  const normalizeCustomContentType = (value: unknown): string => {
    const normalized = toNonEmptyString(value)?.toLowerCase().replace(/_/g, '-');
    if (!normalized) return 'content';
    return CUSTOM_CONTENT_TYPE_ALIASES[normalized] || normalized;
  };

  const normalizeCustomContentUuid = (item: ICustomContentItem): string | null => {
    return (
      toNonEmptyString(item?.content_id) ||
      toNonEmptyString(item?.content_data?.id) ||
      toNonEmptyString(item?.content_data?.objectID)
    );
  };

  const normalizeCustomContentItem = (
    item: ICustomContentItem,
    index: number
  ): ICustomContentListItemView => {
    const uuid = normalizeCustomContentUuid(item);
    return {
      id: toNonEmptyString(item?.id) || uuid || `custom-content-${index}`,
      uuid,
      name: toNonEmptyString(item?.content_data?.name) || uuid || 'Untitled content',
      description: toNonEmptyString(item?.content_data?.description) || '',
      content_type: normalizeCustomContentType(item?.content_type),
    };
  };

  const normalizedCustomContentItems = useMemo(
    () => customContentItems.map((item, index) => normalizeCustomContentItem(item, index)),
    [customContentItems]
  );

  const filteredCustomContentItems = useMemo(() => {
    const selectedTypes = customContentTypeFilters.filter(Boolean);
    const normalizedSearch = customContentSearch.trim().toLowerCase();

    return normalizedCustomContentItems.filter((item) => {
      const isTypeMatch =
        selectedTypes.length === 0 ||
        selectedTypes.includes(normalizeCustomContentType(item.content_type));

      if (!isTypeMatch) return false;
      if (!normalizedSearch) return true;

      const name = item.name.toLowerCase();
      const description = item.description.toLowerCase();
      return name.includes(normalizedSearch) || description.includes(normalizedSearch);
    });
  }, [customContentSearch, customContentTypeFilters, normalizedCustomContentItems]);

  const formatContentTypeLabel = (value: string): string => {
    return value
      .replace(/[-_]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word) => {
        if (word.toLowerCase() === 'ctf') return 'CTF';
        return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
      })
      .join(' ');
  };

  const customContentTypeOptions = useMemo(() => {
    const types = Array.from(
      new Set(normalizedCustomContentItems.map((item) => item.content_type))
    ).filter(Boolean);

    return [
      { label: 'All content', value: '' },
      ...types.map((type) => ({
        label: formatContentTypeLabel(type),
        value: type,
      })),
    ];
  }, [normalizedCustomContentItems]);

  const tabItems = useMemo(() => {
    const items: { name: React.ReactNode; value: string; counter: number }[] = [
      {
        name: 'INE Content',
        value: 'content',
        counter: Object.keys(contentList).length,
      },
    ];

    if (isCustomContentTabEnabled) {
      items.push({
        name: (
          <CustomTrainingTabLabel>
            <CustomTrainingTabLogo
              src={account?.logo_url || AccountDefaultLogo}
              alt="Account logo"
            />
            <span>Custom Training</span>
          </CustomTrainingTabLabel>
        ),
        value: CUSTOM_CONTENT_TAB,
        counter: Object.keys(selectedCustomContent).length,
      });
    }

    items.push({
      name: 'External Link',
      value: 'link',
      counter: Object.keys(linksList).length,
    });

    return items;
  }, [account?.logo_url, contentList, isCustomContentTabEnabled, linksList, selectedCustomContent]);

  useEffect(() => {
    if (!isVisible || !isCustomContentTabEnabled || selectedTab !== CUSTOM_CONTENT_TAB) return;

    let isSubscribed = true;

    const getCustomContent = async () => {
      setCustomContentLoading(true);
      setCustomContentHasError(false);
      try {
        const search = debouncedCustomContentSearch.trim();
        const contentTypeFilters = customContentTypeFilters.filter(Boolean);
        let page = 1;
        let hasNextPage = true;
        let totalPages = 0;
        const results: ICustomContentItem[] = [];

        while (hasNextPage && page <= MAX_CUSTOM_CONTENT_PAGES) {
          const queryParams = {
            page,
            page_size: CUSTOM_CONTENT_PAGE_SIZE,
            ...(search ? { search } : {}),
            ...(contentTypeFilters.length ? { content_type: contentTypeFilters } : {}),
          };
          const response = await getCustomContentList({
            ...queryParams,
          });

          const pageResults = Array.isArray(response?.data?.results) ? response.data.results : [];
          results.push(...pageResults);

          const count = Number(response?.data?.count);
          if (Number.isFinite(count) && count >= 0) {
            totalPages = Math.max(1, Math.ceil(count / CUSTOM_CONTENT_PAGE_SIZE));
          }

          hasNextPage = Boolean(response?.data?.next) || (totalPages ? page < totalPages : false);
          page += 1;
        }

        if (!isSubscribed) return;

        setCustomContentItems(results);
      } catch (_error) {
        if (!isSubscribed) return;
        setCustomContentHasError(true);
        Notification({ text: 'There has been a problem fetching custom content', type: 'error' });
      } finally {
        if (isSubscribed) {
          setCustomContentLoading(false);
        }
      }
    };

    getCustomContent();

    return () => {
      isSubscribed = false;
    };
  }, [
    customContentTypeFilters,
    debouncedCustomContentSearch,
    isCustomContentTabEnabled,
    isVisible,
    selectedTab,
  ]);

  const allValuesPresent = (from: string[], to: string[]) => {
    return from.length > 0 && from.every((v) => to.includes(v));
  };

  const handleFilterChange = (filter, value) => {
    filter === 'content_type' && !allValuesPresent(value, ENABLED_CONTENT_TYPES)
      ? setFilters(() => ({ ...createInitialFilters(), [filter]: value }))
      : setFilters((prevState) => ({ ...prevState, [filter]: value }));
  };

  const handleFilterWithTooltipChange = (value) => {
    if (value !== '') {
      setFilters((prevState) => ({ ...prevState, is_lab_experience: value === 'true' }));
    } else {
      setFilters(() => ({ ...createInitialFilters(), content_type: ['Lab'] }));
    }
  };

  const handleHasSkillCheckFilterChange = (value) => {
    if (value !== '') {
      setFilters((prevState) => ({ ...prevState, has_skill_check_labs: value === 'true' }));
    } else {
      setFilters((prevState) => {
        const newState = { ...prevState };
        delete newState.has_skill_check_labs;
        return newState;
      });
    }
  };
  const onAddLink = async () => {
    let titleSite;
    let descriptionSite;
    let logoSite;

    try {
      const response = await fetch(newLink);
      const text = await response.text();
      const newdoc = document.createRange().createContextualFragment(text);

      titleSite = newdoc.querySelector('meta[property="og:title"]')?.getAttribute('content');

      descriptionSite = newdoc
        .querySelector('meta[property="og:description"]')
        ?.getAttribute('content');

      logoSite = newdoc.querySelector('meta[property="og:image"]')?.getAttribute('content');
    } catch (error) {
      if (error?.response?.data?.details) {
        Notification({ text: error.response.data.details, type: 'error' });
      }
    }
    setLinksList((prevState) => ({
      [uuidv4()]: {
        name: titleSite || newLink,
        description: descriptionSite || '',
        link_url: newLink,
        logo_image_url: logoSite || '',
      },
      ...prevState,
    }));
    setNewLink('');
  };

  const onSaveLink = (objectID: string, link: IPlaylistLink) => {
    setLinksList((prevState) => ({
      ...prevState,
      [objectID]: { ...link },
    }));
  };

  const onDeleteLink = (objectID: string) => {
    setLinksList((prevState) => {
      const newState = { ...prevState };

      delete newState[objectID];

      return newState;
    });
  };

  const generateContentPayload = async () => {
    let normalizedContent = cloneDeep(playlist.content);
    if (!normalizedContent || !normalizedContent.length) {
      normalizedContent = [
        {
          name: FIRST_PLAYLIST_CATEGORY,
          content_type: 'group',
          content: [],
        },
      ];
    }

    for (const section of normalizedContent) {
      section.content = section.content.map(({ uuid, content_type }) => ({
        uuid,
        content_type,
      }));

      if (section.name === category) {
        // add new content
        for (const [key, { content_type }] of Object.entries(contentList)) {
          section.content.push({
            uuid: key,
            content_type,
          });
        }

        // add custom content
        for (const [key, { content_type }] of Object.entries(selectedCustomContent)) {
          section.content.push({
            uuid: key,
            content_type,
          });
        }

        // add new links
        if (Object.keys(linksList).length) {
          const savedLinks: IPlaylistLink[] | undefined = await handleBulkPlaylistLinks();

          if (savedLinks && savedLinks.length) {
            for (const savedLink of savedLinks) {
              section.content.push({
                uuid: savedLink.id,
                content_type: 'link',
              });
            }
          }
        }
      }
    }

    return normalizedContent;
  };

  const handleConfirm = async () => {
    setLoading(true);

    const reqData: IPlaylist = {
      id: playlist.id,
      name: playlist.name,
      color: playlist.color,
      content: await generateContentPayload(),
      business_account_id,
      company: INE_UUID, // INE always
    };

    handleAddContent(reqData);
  };

  const handleBulkPlaylistLinks = async (): Promise<IPlaylistLink[] | undefined> => {
    try {
      const response = await bulkCreateLinks({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        links: Object.entries(linksList).map(([objectID, link]) => link),
        business_account_id,
      });
      if (response.status === 201) {
        Notification({ text: `Links have been created`, type: 'success' });
        return response.data;
      } else {
        Notification({ text: 'There has been a problem creating your links', type: 'error' });
      }
    } catch (error) {
      if (error?.response?.data?.details) {
        Notification({ text: error.response.data.details, type: 'error' });
      }
    }
  };

  const handleAddContent = async (data: IPlaylist) => {
    try {
      const response = await editPlaylist(data);
      if (response.status === 200) {
        Notification({ text: `${response.data.name} has been updated`, type: 'success' });
        onClose(response.data);
        return;
      } else {
        Notification({ text: 'There has been a problem editing your playlist', type: 'error' });
      }
    } catch (error) {
      // The collection validator rejects with {content, reason_code}; other
      // paths use details/detail. Without this the modal closes saving nothing.
      const data = error?.response?.data;
      const message =
        data?.details ||
        (typeof data?.content === 'string' ? data.content : undefined) ||
        data?.detail ||
        'There has been a problem editing your playlist';
      Notification({ text: message, type: 'error' });
    }
    setLoading(false);
    onClose();
  };

  const handleCheckContent = (hit: IContentItem) => {
    const findRow = contentList[hit.objectID];

    if (findRow) {
      setContentList((prevState) => {
        const copiedSelected = { ...prevState };

        delete copiedSelected[hit.objectID];

        return copiedSelected;
      });
    } else {
      setContentList((prevState) => ({
        ...prevState,
        [hit.objectID]: { ...hit },
      }));
    }
  };
  const onRemoveCourse = (courseId) => {
    setContentList((prevState) => {
      const newState = { ...prevState };
      delete newState[courseId];
      return newState;
    });
  };

  const handleCheckCustomContent = (item: ICustomContentListItemView) => {
    if (!item.uuid) return;

    const selectedItem = selectedCustomContent[item.uuid];
    if (selectedItem) {
      setSelectedCustomContent((prevState) => {
        const nextState = { ...prevState };
        delete nextState[item.uuid];
        return nextState;
      });
      return;
    }

    setSelectedCustomContent((prevState) => ({
      ...prevState,
      [item.uuid]: item,
    }));
  };

  const onRemoveCustomContent = (itemId: string) => {
    setSelectedCustomContent((prevState) => {
      const newState = { ...prevState };
      delete newState[itemId];
      return newState;
    });
  };

  return (
    <Modal
      centered
      visible={isVisible}
      fullscreen={isMobile}
      onCancel={() => onClose()}
      width={isMobile ? '100%' : '70vw'}
      footer={null}
      closeIconColor={'#fff'}
      maskClosable={false}
      className="add-content"
    >
      <ModalContainer style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <HeaderContainer>
          <h2>{title}</h2>
        </HeaderContainer>
        <ModalContent grow flexColumn style={{ overflow: 'hidden', minHeight: 0 }}>
          <Tabs
            items={tabItems}
            selected={selectedTab}
            mobileScrollable
            onTabChange={(tab) => setSelectedTab(tab)}
          />

          {selectedTab === 'content' && (
            <ModalSection style={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <AddContentLeftContainer>
                <InstantSearch
                  searchClient={searchClient}
                  indexName={process.env.REACT_APP_ALGOLIA_SEARCH_INDEX}
                >
                  <FiltersWrapper columns="2.7fr 1.3fr">
                    <SearchInput />
                    <InputSelectCheckbox
                      values={filters.content_type}
                      placeHolder={'Content Type'}
                      options={CONTENT_TYPE_OPTIONS}
                      onChange={(v) => handleFilterChange('content_type', v)}
                      confirmBtnLabel={'Apply filter'}
                    />
                  </FiltersWrapper>

                  {allValuesPresent(filters.content_type, ENABLED_CONTENT_TYPES) && (
                    <>
                      <FiltersWrapper columns="1fr 1fr 1fr">
                        <InputSelectCheckbox
                          values={filters['learning_areas.name']}
                          placeHolder={'Learning Area'}
                          options={Object.values(learningAreaOptions)}
                          onChange={(v) => handleFilterChange('learning_areas.name', v)}
                          confirmBtnLabel={'Apply filter'}
                        />
                        <InputSelectCheckbox
                          values={filters.difficulty}
                          placeHolder={'Difficulty'}
                          options={DIFFICULTY_OPTIONS}
                          onChange={(v) => handleFilterChange('difficulty', v)}
                          confirmBtnLabel={'Apply filter'}
                        />
                        <InputSelectCheckbox
                          values={filters['categories.name']}
                          placeHolder={'Category'}
                          options={Object.values(categoryOptions)}
                          onChange={(v) => handleFilterChange('categories.name', v)}
                          confirmBtnLabel={'Apply filter'}
                        />
                      </FiltersWrapper>
                      <FiltersWrapper columns="1fr 1fr">
                        <FilterWithTooltip
                          value={filters['has_skill_check_labs']}
                          options={SKILL_CHECK_OPTIONS}
                          onChange={(v) => handleHasSkillCheckFilterChange(v)}
                        />
                      </FiltersWrapper>
                    </>
                  )}
                  {filters.content_type.length === 1 &&
                    filters.content_type[0] === CONTENT_TYPES.LAB && (
                      <FiltersWrapper columns="1fr 1fr">
                        <FilterWithTooltip
                          value={filters['is_lab_experience']}
                          options={getLabOptions(collectionsEnabled)}
                          onChange={(v) => handleFilterWithTooltipChange(v)}
                        />
                      </FiltersWrapper>
                    )}
                  <Configure facetFilters={facetFilters} page={0} />
                  <SearchResults
                    contentToExclude={contentAlreadyInPlaylist}
                    selectedContent={contentList}
                    onCheckContent={handleCheckContent}
                  />
                </InstantSearch>
              </AddContentLeftContainer>
              <AddContentRightContainer>
                <ContentItemsContainer>
                  <ContainerTitle>Your selections</ContainerTitle>
                  {Object.entries(contentList).map(([uuid, content]) => {
                    return (
                      <CourseItem key={uuid} contentType={content.content_type}>
                        <p>{content.name}</p>
                        <span>{getContentTypeLabel(content)}</span>
                        {content.has_skill_check_labs && (
                          <span className="tag-scl">{HAS_SKILL_CHECK_LAB}</span>
                        )}
                        <IconWrapper onClick={() => onRemoveCourse(uuid)}>
                          <Icon size="small" stroke="#A793F6" fill={'#A793F6'} icon="Close" />
                        </IconWrapper>
                      </CourseItem>
                    );
                  })}
                </ContentItemsContainer>
              </AddContentRightContainer>
            </ModalSection>
          )}

          {selectedTab === CUSTOM_CONTENT_TAB && isCustomContentTabEnabled && (
            <ModalSection style={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <AddContentLeftContainer>
                <FiltersWrapper columns="2.7fr 1.3fr">
                  <StyledInput
                    placeholder="Search custom training"
                    value={customContentSearch}
                    onChange={(e) => setCustomContentSearch(e.target.value)}
                  />
                  <InputSelectCheckbox
                    values={customContentTypeFilters}
                    placeHolder={'Content Type'}
                    options={customContentTypeOptions}
                    onChange={(v) => setCustomContentTypeFilters(v.filter(Boolean))}
                    confirmBtnLabel={'Apply filter'}
                  />
                </FiltersWrapper>

                {customContentLoading ? (
                  <CustomContentLoadingWrapper>
                    <Spinner active top="50%" left="50%" />
                  </CustomContentLoadingWrapper>
                ) : (
                  <CustomContentResults
                    items={filteredCustomContentItems}
                    contentToExclude={contentAlreadyInPlaylist}
                    selectedContent={selectedCustomContent}
                    onCheckContent={handleCheckCustomContent}
                  />
                )}

                {!customContentLoading && customContentHasError && (
                  <p style={{ marginTop: '10px' }}>
                    There was a problem loading custom training content.
                  </p>
                )}
              </AddContentLeftContainer>
              <AddContentRightContainer>
                <ContentItemsContainer>
                  <ContainerTitle>Your selections</ContainerTitle>
                  {Object.entries(selectedCustomContent).map(([uuid, content]) => {
                    return (
                      <CourseItem key={uuid} contentType={content.content_type}>
                        <p>{content.name}</p>
                        <span>{getContentTypeLabel(content)}</span>
                        <IconWrapper onClick={() => onRemoveCustomContent(uuid)}>
                          <Icon size="small" stroke="#A793F6" fill={'#A793F6'} icon="Close" />
                        </IconWrapper>
                      </CourseItem>
                    );
                  })}
                </ContentItemsContainer>
              </AddContentRightContainer>
            </ModalSection>
          )}

          {selectedTab === 'link' && (
            <ModalSection>
              <div className="add-content__add-link-wrapper">
                <StyledInput
                  placeholder="Type or paste url"
                  value={newLink}
                  onChange={(e) => setNewLink(e.target.value)}
                />
                <Button
                  icon="Link"
                  disabled={!newLink.length || !isLinkValid}
                  $secondary
                  onClick={onAddLink}
                >
                  Add link
                </Button>
              </div>
              <div className="add-content__add-link-error-wrapper">
                {showInvalidLink ? (
                  <>
                    <Icon icon="Cancel" />
                    <p>Incorrect URL format. Please try again.</p>
                  </>
                ) : null}
              </div>
              <LinksContainer>
                <ContainerTitle>Your links</ContainerTitle>
                {Object.entries(linksList).map(([id, link]) => {
                  return (
                    <LinkItem
                      key={id}
                      data={link}
                      onSave={(linkToSave) => onSaveLink(id, linkToSave)}
                      onDelete={() => onDeleteLink(id)}
                    />
                  );
                })}
              </LinksContainer>
            </ModalSection>
          )}
        </ModalContent>

        <ModalActions>
          <Button
            disabled={loading}
            $primary
            style={{ marginLeft: 'auto' }}
            onClick={handleConfirm}
          >
            {ctaLabel}
          </Button>
        </ModalActions>
      </ModalContainer>
    </Modal>
  );
};

export default AddContent;
